import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Row,
  Select,
  Space,
} from 'antd';
import {
  CloudDownloadOutlined,
  //FileSearchOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { printPostedTransferPickList, setIsPSInvoking } from 'actions/printActions';
import {
  Label,
  HeaderTitle,
  Loading,
  ScreenMask,
  message,
} from 'components/common';
import SelectWarehouseDialog from 'components/Warehouse/SelectWarehouseDialog';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  //COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  HTTP_STATUS_OK,
  LOADING_ICON_SIZE1,
  LS_DEFAULT_WAREHOUSE_KEY,
} from 'constants/config';
import { RootState } from 'reducers';
import {
  exportPostedTransferPicklistToPdf,
  fetchPostedTransferPicklistQueue,
} from 'services/inventory';
import { fetchPrintTemplates, fetchPrinterList } from 'services/print';
import { convertTimeByUtc, formatPrinterToTree, getWarehouseFromCache } from 'utils';
import { userProfiles } from 'utils/auth';

type Props = {
};

const PrintPickList = (props: Props) => {
  const { useState } = React;
  const [allowReprint, setAllowReprint] = useState(false);
  const [currentPicklist, setCurrentPicklist] = useState<StringKAnyVPair>();
  const [currentPrinter, setCurrentPrinter] = useState<StringKAnyVPair>();
  const [currentTemplate, setCurrentTemplate] = useState<StringKAnyVPair>();
  const [exportMarkAsPrinted, setExportMarkAsPrinted] = useState(false);
  const [inited, setInited] = useState(false);
  const [isLoadingPrinterList, setIsLoadingPrinterList] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [picklistOptions, setPicklistOptions] = useState<StringKAnyVPair[]>([]);
  const [printTemplateOptions, setPrintTemplateOptions] = useState<StringKAnyVPair[]>([]);
  const [printerList, setPrinterList] = useState<StringKAnyVPair[]>([]);
  const [printerTree, setPrinterTree] = useState<StringKAnyVPair[]>([]);
  const [selectWarehouseDialogVisible, setSelectWarehouseDialogVisible] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<StringKAnyVPair>({});
  const dispatch = useDispatch();
  const psIsInvoking = useSelector(
    (state: RootState) => state.sales.printstationIsInvoking,
    shallowEqual
  );

  // eslint-disable-next-line
  const bootstrap = (whNum: number) => {
    const wh = getWarehouseFromCache(whNum);

    if (wh) {
      //onChangeWarehouse(wh);
      setSelectedWarehouse(wh || {});
      loadPicklistTemplate();
      loadPrinterList();
      loadPostedTransferPicklistQueue(wh)
    }
  };

  const closeSelectWarehouseDialog = () => {
    setSelectWarehouseDialogVisible(false);
  };

  const exportPicklistToPdf = async () => {
    const pd: StringKAnyVPair = {
      markPrint: exportMarkAsPrinted ? 1 : 0,
      picklistNum: (currentPicklist || {}).value,
      printTemplateNum: (currentTemplate || {}).value,
      warehouseCode: selectedWarehouse.warehouseCode,
      warehouseNum: selectedWarehouse.warehouseNum,
      allowReprint,
    };

    console.log('p', pd);
    setLockScreen(true);
    try {
      const res = await exportPostedTransferPicklistToPdf(pd) as any;

      if (res) {
        const blob = new Blob([res], { type: 'application/pdf' });
        const fileName = `PickList${pd.picklistNum}.pdf`;
        if ('download' in document.createElement('a')) {
          const elink = document.createElement('a');
          elink.download = fileName;
          elink.style.display = 'none';
          elink.href = URL.createObjectURL(blob);
          document.body.appendChild(elink);
          elink.click();
          URL.revokeObjectURL(elink.href);
          document.body.removeChild(elink);
        } else {
          //navigator.msSaveBlob(blob, fileName);
          message.error({
            content: 'Browser not supported',
            duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
          });
        }
      } else {
        message.error({
          content: 'No pdf data received',
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
      message.error({
        content: `Exported PDF error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const isAllowReprintEnabled = () => {
    return !(currentPicklist && currentPicklist.value === 0);
  };

  const isExportPdfEnabled = () => {
    // need to check template
    return !!(currentPicklist && currentTemplate);
  };

  // eslint-disable-next-line
  const isPreviewEnabled = () => {
    return !!currentPicklist;
  };

  const isPrintPicklistEnabled = () => {
    // need to check template and printer
    return !!(currentPicklist && currentTemplate && currentPrinter);
  };

  const loadPicklistTemplate = async () => {
    setIsLoadingTemplate(true);
    try {
      const profile = userProfiles.profile;
      const res = await fetchPrintTemplates({
        TemplateType: 17,
        MasterAccountNum: profile.masterAccountNum,
        ProfileNum: profile.profileNum,
      });
      //console.log('ts', res);
      if (res.code === HTTP_STATUS_OK && Array.isArray(res.data)) {
        setPrintTemplateOptions(
          res.data.map((e: any) => ({
            label: e.templateName,
            value: e.printTemplateNum,
          }))
        );
      }
    } catch(e) {
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const loadPrinterList = async () => {
    setIsLoadingPrinterList(true);
    try {
      const res = await fetchPrinterList();
      //console.log('p', res);
      if (res.code === HTTP_STATUS_OK && Array.isArray(res.data)) {
        setPrinterList(res.data);
        setPrinterTree(formatPrinterToTree(res.data));

        /*const defaultPrinterNum = localStorage.getItem('TheLastPrinter')

        if (defaultPrinterNum){
          const temp = res.data.filter((i:any)=>i.printerNum === Number(defaultPrinterNum));
          if (temp.length > 0){
            //form.setFieldsValue({PrinterNum: temp[0].printerNum});
            setCurrentPrinter(temp[0]);
          }
        }*/
      }
    } catch(e) {
    } finally {
      setIsLoadingPrinterList(false);
    }
  };

  const loadPostedTransferPicklistQueue = async (wh = selectedWarehouse) => {
    setLockScreen(true);
    try {
      const data = await fetchPostedTransferPicklistQueue({warehouseNum: wh.warehouseNum});
      //console.log('r', data);
      if (data.length > 0) {
        setPicklistOptions(
          data.map(e => ({
            //...e,
            label: e.warehouseTransferPickListNum === 0 ? 'All' : `${e.sessionId} on ${convertTimeByUtc(e.createDate)}`,
            value: e.warehouseTransferPickListNum,
          }))
        );
      }
    } catch(e) {
      message.error({
        content: `Loaded transfer picklist queue error ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const onChangeAllowReprint = (evt: any) => {
    setAllowReprint(evt.target.checked);
  };

  const onChangeExportMarkAsPrinted = (evt: any) => {
    setExportMarkAsPrinted(evt.target.checked);
  };

  const onChangePicklist = (pn: any) => {
    const pl = picklistOptions.find(e => e.value === pn);

    setCurrentPicklist(pl || undefined);
    (pn === 0) && setAllowReprint(false);
  };

  const onChangePrinter = (pn: any) => {
    const t = printerList.find(e => e.printerNum === pn);

    setCurrentPrinter(t || undefined);
  };

  const onChangeTemplate = (tn: any) => {
    const t = printTemplateOptions.find(e => e.value === tn);

    setCurrentTemplate(t || undefined);
  };

  const onSelectWarehouse = (wh: StringKAnyVPair) => {
    //console.log('w', wh);
    if (wh) {
      localStorage.setItem(LS_DEFAULT_WAREHOUSE_KEY, wh.id);
      window.location.reload();
    }
  };

  const printCurrentPostedTransferPicklist = () => {
    const profile = userProfiles.profile;
    const pd: StringKAnyVPair = {
      //markPrint: exportMarkAsPrinted ? 1 : 0,
      masterAccountNum: profile.masterAccountNum,
      picklistNum: (currentPicklist || {}).value,
      printTemplateNum: (currentTemplate || {}).value,
      printerAccountNum: (currentPrinter || {}).printerAccountNum,
      printerNum: (currentPrinter || {}).printerNum,
      profileNum: profile.profileNum,
      //user: profile.email,
      warehouseCode: selectedWarehouse.warehouseCode,
      warehouseNum: selectedWarehouse.warehouseNum,
      allowReprint,
    };

    console.log('pd', pd);
    dispatch(setIsPSInvoking(true));
    dispatch(printPostedTransferPickList(pd));
  };

  React.useEffect(() => {
    if (!inited) {
      const whNum = parseInt(
        localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY) || '-1'
      );

      if (whNum > 0 /*&& whid*/) {
        bootstrap(whNum);
      } else {
        setSelectWarehouseDialogVisible(true);
      }
      setInited(true);
    }
  }, [bootstrap, inited]);

  return (<>
    <HeaderTitle breadcrumb={['Warehouse Transfer', 'ERP Posted Transfer', 'Print Pick List']} />
    <Card title="Print Settings" bordered={false}>
      <Row align="middle">
        <Col style={{ paddingLeft: 6, paddingRight: 6 }}>
          <Row align="middle">
            <Label className="label-bold tail-colon2">Pick List</Label>
            <Select
              allowClear
              onChange={onChangePicklist}
              options={picklistOptions}
              style={{ width: 'calc(100% - 72px)', minWidth: 500 }}
            />
          </Row>
        </Col>
        <Col style={{ paddingLeft: 6, paddingRight: 6 }}>
          <Checkbox
            checked={allowReprint}
            disabled={!isAllowReprintEnabled()}
            onChange={onChangeAllowReprint}
          >
            Allow Reprint
          </Checkbox>
        </Col>
      </Row>
      <Row align="middle" className="mt-8" justify="center">
        <Col>
          <div>
            <Label className="label-bold tail-colon2">Select A Picklist Template</Label>
            <Row className="mt-4">
                <Space>
                <Button
                  className="p-4-8"
                  onClick={loadPicklistTemplate}
                >
                  <ReloadOutlined style={{ color: 'green' }} />
                </Button>
                <Select
                  loading={isLoadingTemplate}
                  onChange={onChangeTemplate}
                  options={printTemplateOptions}
                  placeholder="Select a print template"
                  style={{ width: 300 }}
                  value={(currentTemplate || {}).value}
                />
              </Space>
            </Row>
          </div>
          <div className="mt-4">
            <Label className="label-bold tail-colon2">Printer</Label>
            <Row className="mt-4">
              <Space>
                <Button
                  className="p-4-8"
                  onClick={loadPrinterList}
                >
                  <ReloadOutlined style={{ color: 'green' }} />
                </Button>
                <Select
                  loading={isLoadingPrinterList}
                  onChange={onChangePrinter}
                  placeholder="Select a printer"
                  style={{ width: 300 }}
                >
                  {printerTree.map((item) => {
                    return (
                      <Select.OptGroup key={item.label} label={item.label}>
                        {item.options.map((citem: any) => {
                          return (
                            <Select.Option
                              key={citem.value}
                              value={citem.value}
                              label={`${item.label} - ${citem.label}`}
                            >
                              {citem.label}
                            </Select.Option>
                          );
                        })}
                      </Select.OptGroup>
                    );
                  })}
                </Select>
              </Space>
            </Row>
          </div>
        </Col>
      </Row>
      <Row align="middle" className="mt-8" justify="center">
        <Space>
          {/*<Button
            disabled={!isPreviewEnabled()}
          >
            <FileSearchOutlined />
            Preview
          </Button>*/}
          <Button
            disabled={!isPrintPicklistEnabled()}
            onClick={printCurrentPostedTransferPicklist}
            type="primary"
          >
            <PrinterOutlined />
            Print Picklist
          </Button>
          <Button
            disabled={!isExportPdfEnabled()}
            onClick={exportPicklistToPdf}
          >
            <CloudDownloadOutlined />
            Export to PDF
          </Button>
          <Checkbox
            checked={exportMarkAsPrinted}
            disabled={!isExportPdfEnabled()}
            onChange={onChangeExportMarkAsPrinted}
          >
            Export PDF and Mark as Printed
          </Checkbox>
        </Space>
      </Row>
    </Card>
    {selectWarehouseDialogVisible && (
      <SelectWarehouseDialog
        defaultEnable
        //defaultValue={recommendWarehouseNum}
        onClose={closeSelectWarehouseDialog}
        onSelect={onSelectWarehouse}
        title="Select default warehouse"
        visible={selectWarehouseDialogVisible}
      />
    )}
    {(lockScreen || psIsInvoking) && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default PrintPickList;
